import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  type EmployeeProfile,
  getEmployeeProfile,
  updateEmployeeProfile,
} from '../shared/api/employee.service';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import { hasRenderableAvatarUrl } from '../shared/model';

import { RegistrationCompletePageContent } from './registration-complete-page-content';

type RegistrationCompleteState = 'congrats' | 'card';

export const DEFAULT_REGISTRATION_COMPLETE_STATE: RegistrationCompleteState =
  'card';

const REGISTRATION_COMPLETE_STATE_SEARCH_PARAM = 'state';
const REGISTRATION_COMPLETE_PHOTO_STATE_PARAM_VALUE = 'photo';

export function resolveRegistrationCompleteState(
  search: string,
  hasUploadedAvatar = false,
): RegistrationCompleteState {
  const searchParams = new URLSearchParams(search);
  const state = searchParams.get(REGISTRATION_COMPLETE_STATE_SEARCH_PARAM);

  if (state === 'card') {
    return 'card';
  }

  if (state === REGISTRATION_COMPLETE_PHOTO_STATE_PARAM_VALUE) {
    return 'congrats';
  }

  return hasUploadedAvatar ? 'congrats' : DEFAULT_REGISTRATION_COMPLETE_STATE;
}

function hasUploadedAvatar(profile: EmployeeProfile | null): boolean {
  return hasRenderableAvatarUrl(profile?.avatarUrl);
}

export function CongratsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { status } = useEmployeeSession();
  const queryClient = useQueryClient();
  const cachedProfile =
    queryClient.getQueryData<EmployeeProfile>(['employee', 'profile']) ?? null;
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const profileSave = useMutation({
    mutationFn: updateEmployeeProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['employee', 'profile'],
        refetchType: 'all',
      });
    },
  });
  const profile = profileQuery.data ?? cachedProfile;
  const state = resolveRegistrationCompleteState(
    location.search,
    hasUploadedAvatar(profile),
  );

  return (
    <RegistrationCompletePageContent
      hasUploadedAvatar={hasUploadedAvatar(profile)}
      isContinuing={profileSave.isPending}
      submitError={profileSave.isError ? t('login.networkError') : null}
      state={state}
      onContinue={async () => {
        try {
          await profileSave.mutateAsync({ isOnboarded: true });
        } catch {
          return;
        }

        navigate('/home');
      }}
    />
  );
}
